import { useState, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  CheckCircle2, Loader2, AlertTriangle, Upload, X, ArrowLeft,
  ArrowRight, Building2, Users, GitBranch, BarChart2, Play,
} from "lucide-react";
import { invokeFunction } from "@/integrations/supabase/client";
import { parseCSV } from "@/lib/learnerHub/utils";
import {
  REC_ALL_UNITS, REC_DEMO_SITES, REC_MOCK_REC, ROLE_PROFILES, unitById,
} from "@/lib/learnerHub/constants";
import type { RecSite, RecResult, LearnerProfile } from "@/lib/learnerHub/types";

// ─── Types ────────────────────────────────────────────────────────────────────

type Step = "upload" | "reconciling" | "review" | "unified";

type SiteProgress = { done: boolean; error: string | null; count: number; running: boolean };

interface RecState {
  step: Step;
  sites: RecSite[];
  rec: Record<number, RecResult[]>;
  ovr: Record<string, string | null>;
  conf: Record<string, boolean>;
  activeTab: number;
  selectedRole: string;
  progress: Record<number, SiteProgress>;
  globalErr: string | null;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const SITE_COLORS = ["hsl(210,80%,52%)", "hsl(142,60%,42%)", "hsl(38,90%,50%)", "hsl(0,72%,50%)"];
const SITE_BG    = ["hsl(210,80%,95%)", "hsl(142,60%,94%)", "hsl(38,90%,94%)", "hsl(0,72%,94%)"];

const INITIAL_SITES: RecSite[] = [0, 1, 2, 3].map((i) => ({
  id: i, name: "", csvData: null, vocab: [], qCol: null, nCol: null, iCol: null, rCol: null,
}));

const INITIAL_STATE: RecState = {
  step: "upload", sites: INITIAL_SITES, rec: {}, ovr: {}, conf: {},
  activeTab: 0, selectedRole: Object.keys(ROLE_PROFILES)[0],
  progress: {}, globalErr: null,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function detectCol(headers: string[], patterns: string[]): string | null {
  return headers.find((h) => patterns.some((p) => new RegExp(p, "i").test(h))) ?? null;
}

function extractVocab(csvData: { headers: string[]; rows: string[][] }, qCol: string): string[] {
  const idx = csvData.headers.indexOf(qCol);
  if (idx < 0) return [];
  return [...new Set(csvData.rows.map((r) => r[idx]).filter(Boolean))];
}

function resolvedUCL(siteId: number, qual: string, rec: Record<number, RecResult[]>, ovr: Record<string, string | null>): string | null {
  const k = `${siteId}:${qual}`;
  if (ovr[k] !== undefined) return ovr[k];
  return (rec[siteId] || []).find((r) => r.qual === qual)?.uclId ?? null;
}

function isConfirmed(siteId: number, qual: string, rec: Record<number, RecResult[]>, conf: Record<string, boolean>): boolean {
  const k = `${siteId}:${qual}`;
  if (conf[k]) return true;
  return (rec[siteId] || []).find((r) => r.qual === qual)?.confidence === "HIGH";
}

function buildProfiles(
  sites: RecSite[],
  rec: Record<number, RecResult[]>,
  ovr: Record<string, string | null>,
): Array<{ name: string; id: string; role: string; site: string; siteId: number; ucls: Set<string> }> {
  const people: Record<string, { name: string; id: string; role: string; site: string; siteId: number; ucls: Set<string> }> = {};

  sites.filter((s) => s.csvData && rec[s.id]).forEach((site) => {
    const map: Record<string, string | null> = {};
    (rec[site.id] || []).forEach((r) => {
      const k = `${site.id}:${r.qual}`;
      map[r.qual] = ovr[k] !== undefined ? ovr[k] : r.uclId;
    });

    const H = site.csvData!.headers;
    const nI = H.indexOf(site.nCol ?? "");
    const iI = H.indexOf(site.iCol ?? "");
    const rI = H.indexOf(site.rCol ?? "");
    const qI = H.indexOf(site.qCol ?? "");

    site.csvData!.rows.forEach((row) => {
      const pk = (iI >= 0 ? row[iI] : (nI >= 0 ? row[nI] : "?")) + "@" + site.id;
      if (!people[pk]) {
        people[pk] = {
          name: nI >= 0 ? row[nI] : "?",
          id:   iI >= 0 ? row[iI] : "",
          role: rI >= 0 ? row[rI] : "",
          site: site.name,
          siteId: site.id,
          ucls: new Set(),
        };
      }
      const qual = qI >= 0 ? row[qI] : "";
      const uclId = map[qual];
      if (uclId) people[pk].ucls.add(uclId);
    });
  });

  return Object.values(people);
}

function getInitials(name: string) {
  return name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
}

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  onExport: (profiles: LearnerProfile[]) => void;
  onViewProfile?: (employeeId: string) => void;
}

export default function ReconciliationTab({ onExport, onViewProfile }: Props) {
  const [s, setS] = useState<RecState>(INITIAL_STATE);
  const recRef = useRef<Record<number, RecResult[]>>({});

  const update = useCallback((patch: Partial<RecState>) => {
    setS((prev) => ({ ...prev, ...patch }));
  }, []);

  // ─── Column detection for uploaded CSVs ───────────────────────────────────

  function detectColumns(headers: string[]): Pick<RecSite, "qCol" | "nCol" | "iCol" | "rCol"> {
    return {
      qCol: detectCol(headers, ["calificacion", "competency", "qualification_title", "nombrecurso",
        "nombrecompetencia", "cert_name", "trainingmodule", "habilidad", "skill"]) ?? headers[4] ?? headers[0],
      nCol: detectCol(headers, ["nombrecompleto", "fullname", "nombre", "employee_name", "trabajador"]) ?? headers[0],
      iCol: detectCol(headers, ["rut", "employeeid", "legajo", "employee_no", "numeroempleado",
        "staffid", "id_empleado", "staff_id"]) ?? headers[1],
      rCol: detectCol(headers, ["cargo", "role", "job_title", "puesto", "jobtitle"]) ?? headers[3],
    };
  }

  // ─── Load demo site ────────────────────────────────────────────────────────

  function loadDemoSite(idx: number) {
    const demo = REC_DEMO_SITES[idx];
    const csvData = parseCSV(demo.csv);
    const vocab = extractVocab(csvData, demo.qCol);
    const updated = s.sites.map((site) =>
      site.id === idx
        ? { ...site, name: demo.name, csvData, vocab, qCol: demo.qCol, nCol: demo.nCol, iCol: demo.iCol, rCol: demo.rCol }
        : site
    );
    update({ sites: updated, globalErr: null });
  }

  // ─── Load uploaded CSV ─────────────────────────────────────────────────────

  async function handleFileUpload(idx: number, file: File) {
    try {
      const text = await file.text();
      const csvData = parseCSV(text);
      const cols = detectColumns(csvData.headers);
      const vocab = cols.qCol ? extractVocab(csvData, cols.qCol) : [];
      const name = s.sites[idx].name || file.name.replace(/\.csv$/i, "");
      const updated = s.sites.map((site) =>
        site.id === idx ? { ...site, name, csvData, vocab, ...cols } : site
      );
      update({ sites: updated });
    } catch {
      update({ globalErr: `Failed to parse file for Site ${idx + 1}` });
    }
  }

  // ─── AI reconciliation ─────────────────────────────────────────────────────

  async function reconcileSiteAI(site: RecSite): Promise<RecResult[]> {
    const uclList = REC_ALL_UNITS.map((u) => `${u.id}: ${u.label}`).join("\n");
    const vocabList = site.vocab.map((v) => `- "${v}"`).join("\n");

    const prompt = `You are a mining HR specialist. Map these employee qualifications from ${site.name} to CCM-Eleva units.

CCM-Eleva units:
${uclList}

Qualifications held at ${site.name}:
${vocabList}

Return ONLY a JSON array — no markdown, no commentary:
[{"qual":"exact qualification name","uclId":"UCL-001","confidence":"HIGH","rationale":"max 10 words"}]

Rules:
- confidence: HIGH = clear match, MEDIUM = plausible but ambiguous, LOW = weak or none
- Set uclId to null and confidence to LOW if no reasonable match
- Match on the competency being demonstrated, not surface keywords
- Qualifications may be in Spanish or English — match across languages`;

    const { data, error } = await invokeFunction("hub-assistant", {
      body: { prompt, maxTokens: 1500 },
    });
    if (error) throw new Error(typeof error === "string" ? error : (error as any).message);
    const raw: string = (data as any).text || "";
    try {
      return JSON.parse(raw);
    } catch {
      const m = raw.match(/\[[\s\S]*\]/);
      if (m) return JSON.parse(m[0]);
      throw new Error("Could not parse AI response");
    }
  }

  // ─── Run live AI reconciliation ────────────────────────────────────────────

  async function runLive() {
    const loaded = s.sites.filter((s) => s.csvData && s.vocab.length);
    if (loaded.length < 2) return;

    const initProgress: Record<number, SiteProgress> = {};
    loaded.forEach((s) => { initProgress[s.id] = { done: false, error: null, count: 0, running: false }; });
    recRef.current = {};
    update({ step: "reconciling", progress: initProgress, globalErr: null, rec: {} });

    for (const site of loaded) {
      setS((prev) => ({
        ...prev,
        progress: { ...prev.progress, [site.id]: { done: false, error: null, count: 0, running: true } },
      }));
      try {
        const result = await reconcileSiteAI(site);
        recRef.current[site.id] = result;
        setS((prev) => ({
          ...prev,
          rec: { ...prev.rec, [site.id]: result },
          progress: { ...prev.progress, [site.id]: { done: true, error: null, count: result.length, running: false } },
        }));
      } catch (e: any) {
        setS((prev) => ({
          ...prev,
          progress: { ...prev.progress, [site.id]: { done: true, error: e.message, count: 0, running: false } },
        }));
      }
    }

    setS((prev) => ({ ...prev, step: "review", activeTab: loaded[0].id }));
  }

  // ─── Run demo mode ─────────────────────────────────────────────────────────

  function runDemo() {
    const demoSites: RecSite[] = [0, 1, 2, 3].map((i) => {
      const demo = REC_DEMO_SITES[i];
      const csvData = parseCSV(demo.csv);
      const vocab = extractVocab(csvData, demo.qCol);
      return { id: i, name: demo.name, csvData, vocab, qCol: demo.qCol, nCol: demo.nCol, iCol: demo.iCol, rCol: demo.rCol };
    });
    update({ sites: demoSites, rec: REC_MOCK_REC, step: "review", activeTab: 0, ovr: {}, conf: {}, globalErr: null });
  }

  // ─── Export to learner hub ─────────────────────────────────────────────────

  function exportToHub() {
    const profiles = buildProfiles(s.sites, s.rec, s.ovr);
    const converted: LearnerProfile[] = profiles.map((p) => ({
      id: `REC-${p.id}-${Date.now()}`,
      name: p.name,
      employeeId: p.id,
      site: p.site,
      role: p.role,
      source: "excel" as const,
      importedAt: new Date().toISOString().split("T")[0],
      completions: [...p.ucls].map((uclId, ci) => ({
        id: `RUCC-${p.id}-${ci}`,
        rawCourseName: uclId,
        normalizedCourseName: unitById(uclId)?.label || uclId,
        completionDate: new Date().toISOString().split("T")[0],
        expiryDate: null,
        issuingBody: p.site,
        score: null,
        status: "active" as const,
        mappedUnits: [{ unitId: uclId, confidence: "HIGH" as const, method: "manual" as const }],
      })),
    }));
    onExport(converted);
  }

  // ─── Derived ──────────────────────────────────────────────────────────────

  const loadedCount = s.sites.filter((site) => site.csvData && site.vocab.length).length;
  const loadedSites = s.sites.filter((site) => site.csvData && s.rec[site.id]);

  // Stats for review step
  const activeResults = s.rec[s.activeTab] || [];
  const highCount   = activeResults.filter((r) => r.confidence === "HIGH").length;
  const medCount    = activeResults.filter((r) => r.confidence === "MEDIUM").length;
  const lowCount    = activeResults.filter((r) => r.confidence === "LOW" || !r.uclId).length;

  // Profiles for unified step
  const unifiedProfiles = buildProfiles(s.sites, s.rec, s.ovr);
  const totalUCLs = new Set(unifiedProfiles.flatMap((p) => [...p.ucls])).size;

  // Gap analysis
  const roleUCLs = ROLE_PROFILES[s.selectedRole] || [];

  // ─── Render ───────────────────────────────────────────────────────────────

  // Step 1: Upload
  if (s.step === "upload") return (
    <div className="space-y-6">
      {/* Demo banner */}
      <div className="flex items-center justify-between gap-4 p-3 rounded-lg bg-muted border">
        <p className="text-sm text-muted-foreground">
          Load data from up to four mine sites, then reconcile qualifications to CCM-Eleva units using AI.
        </p>
        <Button size="sm" onClick={runDemo} className="shrink-0 gap-1.5">
          <Play className="h-3.5 w-3.5" />
          Demo mode
        </Button>
      </div>

      {s.globalErr && (
        <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {s.globalErr}
        </div>
      )}

      {/* 2×2 site grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {s.sites.map((site) => (
          <SiteCard
            key={site.id}
            site={site}
            onLoadDemo={() => loadDemoSite(site.id)}
            onFileUpload={(f) => handleFileUpload(site.id, f)}
            onNameChange={(name) => update({ sites: s.sites.map((x) => x.id === site.id ? { ...x, name } : x) })}
            onClear={() => update({ sites: s.sites.map((x) => x.id === site.id ? { ...x, csvData: null, vocab: [], qCol: null, nCol: null, iCol: null, rCol: null } : x) })}
          />
        ))}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{loadedCount} of 4 sites loaded</p>
        <Button disabled={loadedCount < 2} onClick={runLive} className="gap-1.5">
          Reconcile with AI
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );

  // Step 2: Reconciling (progress view)
  if (s.step === "reconciling") return (
    <div className="space-y-4 max-w-xl mx-auto">
      <h3 className="font-semibold text-lg">Mapping qualifications to CCM-Eleva…</h3>
      <Card>
        <CardContent className="pt-4 space-y-3">
          {s.sites.filter((site) => site.csvData && site.vocab.length).map((site) => {
            const p = s.progress[site.id];
            return (
              <div key={site.id} className="flex items-center gap-3">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: SITE_COLORS[site.id] }} />
                <span className="flex-1 text-sm font-medium">{site.name || `Site ${site.id + 1}`}</span>
                <span className="text-sm text-muted-foreground">
                  {!p ? "Queued" : p.running ? `Mapping ${site.vocab.length} qualifications…` : p.error ? `Error: ${p.error}` : `${p.count} mapped`}
                </span>
                <span className="w-5 h-5 shrink-0 flex items-center justify-center">
                  {!p || (!p.running && !p.done) ? null :
                   p.running ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> :
                   p.error ? <AlertTriangle className="h-4 w-4 text-destructive" /> :
                   <CheckCircle2 className="h-4 w-4 text-green-600" />}
                </span>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );

  // Step 3: Review
  if (s.step === "review") {
    const activeSite = s.sites.find((x) => x.id === s.activeTab);
    const needsReview = activeResults.filter((r) =>
      !isConfirmed(s.activeTab, r.qual, s.rec, s.conf) || r.confidence !== "HIGH"
    ).length;

    return (
      <div className="space-y-5">
        {/* Stats row */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: "Auto-confirmed", value: highCount, color: "text-green-700 bg-green-50 border-green-200" },
            { label: "Needs review",   value: medCount,  color: "text-amber-700 bg-amber-50 border-amber-200" },
            { label: "No match",       value: lowCount,  color: "text-red-700 bg-red-50 border-red-200" },
          ].map(({ label, value, color }) => (
            <div key={label} className={`rounded-lg border p-3 text-center ${color}`}>
              <div className="text-2xl font-bold">{value}</div>
              <div className="text-xs font-medium">{label}</div>
            </div>
          ))}
        </div>

        {/* Site tabs */}
        <div className="flex gap-1 border-b">
          {loadedSites.map((site) => (
            <button
              key={site.id}
              onClick={() => update({ activeTab: site.id })}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                s.activeTab === site.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <span className="w-2 h-2 rounded-full" style={{ background: SITE_COLORS[site.id] }} />
              {site.name}
            </button>
          ))}
        </div>

        {/* Status line */}
        <p className="text-sm text-muted-foreground">
          <span className="font-medium text-foreground">{activeSite?.name}</span> — qualifications mapped to CCM-Eleva.{" "}
          {needsReview > 0
            ? <span className="text-amber-600 font-medium">{needsReview} item{needsReview > 1 ? "s" : ""} need your review below.</span>
            : <span className="text-green-600 font-medium">All resolved — ready to apply.</span>}
        </p>

        {/* Mapping table */}
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr>
                <th className="text-left px-3 py-2 font-medium text-muted-foreground">Qualification held</th>
                <th className="text-left px-3 py-2 font-medium text-muted-foreground">Maps to (CCM-Eleva)</th>
                <th className="text-left px-3 py-2 font-medium text-muted-foreground w-24">Confidence</th>
                <th className="text-left px-3 py-2 font-medium text-muted-foreground hidden md:table-cell">Rationale</th>
                <th className="text-left px-3 py-2 font-medium text-muted-foreground w-36">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {activeResults.map((r) => {
                const k = `${s.activeTab}:${r.qual}`;
                const confirmed = isConfirmed(s.activeTab, r.qual, s.rec, s.conf);
                const ovrVal = s.ovr[k];
                const effectiveUclId = ovrVal !== undefined ? ovrVal : r.uclId;
                const unit = effectiveUclId ? unitById(effectiveUclId) ?? REC_ALL_UNITS.find(u => u.id === effectiveUclId) : null;
                const rowBg =
                  r.confidence === "MEDIUM" ? "bg-amber-50/50 dark:bg-amber-950/20" :
                  (r.confidence === "LOW" || !r.uclId) ? "bg-red-50/50 dark:bg-red-950/20" : "";

                return (
                  <tr key={r.qual} className={rowBg}>
                    <td className="px-3 py-2.5 align-top">
                      <span className="font-medium">{r.qual}</span>
                    </td>
                    <td className="px-3 py-2.5 align-top">
                      {unit ? (
                        <>
                          <span className="font-semibold">{unit.id}</span>
                          <br />
                          <span className="text-xs text-muted-foreground">{unit.label}</span>
                        </>
                      ) : (
                        <span className="text-destructive text-xs">No match found</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 align-top">
                      <ConfBadge confidence={r.confidence} />
                    </td>
                    <td className="px-3 py-2.5 align-top text-xs text-muted-foreground hidden md:table-cell">
                      {r.rationale}
                    </td>
                    <td className="px-3 py-2.5 align-top">
                      {confirmed && ovrVal === undefined ? (
                        <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                          <CheckCircle2 className="h-3.5 w-3.5" /> Confirmed
                        </span>
                      ) : (
                        <div className="flex flex-col gap-1.5">
                          {!confirmed && (
                            <Button size="sm" variant="outline" className="h-7 text-xs"
                              onClick={() => update({ conf: { ...s.conf, [k]: true } })}>
                              Accept
                            </Button>
                          )}
                          <Select
                            value={ovrVal !== undefined ? (ovrVal ?? "none") : ""}
                            onValueChange={(v) => {
                              const val = v === "none" ? null : v;
                              update({ ovr: { ...s.ovr, [k]: val }, conf: { ...s.conf, [k]: true } });
                            }}
                          >
                            <SelectTrigger className="h-7 text-xs">
                              <SelectValue placeholder="Override…" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">No match</SelectItem>
                              {REC_ALL_UNITS.map((u) => (
                                <SelectItem key={u.id} value={u.id}>{u.id}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between pt-2">
          <Button variant="outline" onClick={() => update({ step: "upload" })} className="gap-1.5">
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
          <Button onClick={() => update({ step: "unified" })} className="gap-1.5">
            Build unified profiles <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  // Step 4: Unified profiles
  return (
    <div className="space-y-5">
      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { icon: Building2, label: "Sites unified",      value: loadedSites.length },
          { icon: Users,     label: "Total employees",    value: unifiedProfiles.length },
          { icon: GitBranch, label: "UCLs represented",   value: totalUCLs },
          { icon: BarChart2, label: "Framework",          value: "CCM-Eleva", isText: true },
        ].map(({ icon: Icon, label, value, isText }) => (
          <Card key={label} className="text-center">
            <CardContent className="pt-4 pb-3">
              <Icon className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
              <div className={isText ? "text-base font-semibold" : "text-2xl font-bold"}>{value}</div>
              <div className="text-xs text-muted-foreground">{label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Site legend */}
      <div className="flex flex-wrap gap-3">
        {loadedSites.map((site) => (
          <span key={site.id} className="flex items-center gap-1.5 text-sm">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: SITE_COLORS[site.id] }} />
            {site.name}
          </span>
        ))}
      </div>

      {/* Two-column grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        {/* Unified profiles */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Unified competency profiles</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-y-auto max-h-[480px]">
              {unifiedProfiles.map((p, i) => (
                <div
                  key={i}
                  className={`flex flex-col gap-1.5 px-4 py-3 border-b last:border-0 hover:bg-muted/30 transition-colors ${onViewProfile ? "cursor-pointer" : ""}`}
                  onClick={() => onViewProfile?.(p.id)}
                >
                  <div className="flex items-center gap-2.5">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
                      style={{ background: SITE_COLORS[p.siteId] }}
                    >
                      {getInitials(p.name)}
                    </div>
                    <div className="min-w-0">
                      <p className={`font-medium text-sm truncate ${onViewProfile ? "hover:underline" : ""}`}>{p.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{p.role} — {p.site}</p>
                    </div>
                    <span className="ml-auto text-xs text-muted-foreground shrink-0">{p.ucls.size} UCL{p.ucls.size !== 1 ? "s" : ""}</span>
                  </div>
                  <div className="flex flex-wrap gap-1 pl-10">
                    {[...p.ucls].sort().map((uclId) => (
                      <Badge key={uclId} variant="secondary" className="text-xs h-5 px-1.5">
                        {uclId}
                      </Badge>
                    ))}
                    {p.ucls.size === 0 && (
                      <span className="text-xs text-muted-foreground italic">No UCLs mapped</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Gap analysis */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <CardTitle className="text-base">Gap analysis</CardTitle>
              <Select value={s.selectedRole} onValueChange={(v) => update({ selectedRole: v })}>
                <SelectTrigger className="h-8 text-xs w-52">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.keys(ROLE_PROFILES).map((r) => (
                    <SelectItem key={r} value={r} className="text-xs">{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto overflow-y-auto max-h-[460px]">
              <table className="w-full text-xs min-w-max">
                <thead className="bg-muted sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Employee</th>
                    {roleUCLs.map((uid) => (
                      <th key={uid} className="px-2 py-2 font-medium text-center">{uid}</th>
                    ))}
                    <th className="px-3 py-2 font-medium text-center">Score</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {unifiedProfiles.map((p, i) => {
                    const covered = roleUCLs.filter((uid) => p.ucls.has(uid)).length;
                    const scoreColor = covered === roleUCLs.length ? "text-green-600" : covered > 0 ? "text-amber-600" : "text-red-600";
                    return (
                      <tr key={i} className="hover:bg-muted/20">
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: SITE_COLORS[p.siteId] }} />
                            <span className="font-medium truncate max-w-[120px]">{p.name}</span>
                          </div>
                        </td>
                        {roleUCLs.map((uid) => (
                          <td key={uid} className="px-2 py-2 text-center">
                            {p.ucls.has(uid)
                              ? <span className="text-green-600 font-bold">✓</span>
                              : <span className="text-muted-foreground">–</span>}
                          </td>
                        ))}
                        <td className={`px-3 py-2 text-center font-semibold ${scoreColor}`}>
                          {covered}/{roleUCLs.length}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Navigation + export */}
      <div className="flex items-center justify-between pt-2">
        <Button variant="outline" onClick={() => update({ step: "review" })} className="gap-1.5">
          <ArrowLeft className="h-4 w-4" /> Back to review
        </Button>
        <Button onClick={exportToHub} className="gap-1.5">
          <Users className="h-4 w-4" />
          Export {unifiedProfiles.length} profiles to learner hub
        </Button>
      </div>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function ConfBadge({ confidence }: { confidence: string }) {
  const map: Record<string, string> = {
    HIGH:   "bg-green-100 text-green-800 border-green-200",
    MEDIUM: "bg-amber-100 text-amber-800 border-amber-200",
    LOW:    "bg-red-100 text-red-800 border-red-200",
  };
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold border ${map[confidence] || map.LOW}`}>
      {confidence}
    </span>
  );
}

function SiteCard({
  site, onLoadDemo, onFileUpload, onNameChange, onClear,
}: {
  site: RecSite;
  onLoadDemo: () => void;
  onFileUpload: (f: File) => void;
  onNameChange: (name: string) => void;
  onClear: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState(false);

  return (
    <Card className="relative">
      <CardContent className="pt-4 space-y-3">
        {/* Header */}
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: SITE_COLORS[site.id] }} />
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Site {site.id + 1}
          </span>
        </div>

        {/* Site name */}
        <Input
          value={site.name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="Site name (e.g. Mina Collahuasi)"
          className="h-8 text-sm"
        />

        {/* Empty state */}
        {!site.csvData ? (
          <div
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files[0]; if (f) onFileUpload(f); }}
            className={`border-2 border-dashed rounded-lg p-4 text-center space-y-2 transition-colors ${drag ? "border-primary bg-primary/5" : "border-muted"}`}
          >
            <Upload className="h-5 w-5 mx-auto text-muted-foreground" />
            <p className="text-xs text-muted-foreground">Drop a CSV here or</p>
            <div className="flex justify-center gap-2">
              <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => fileRef.current?.click()}>
                Browse
              </Button>
              <Button variant="secondary" size="sm" className="h-7 text-xs" onClick={onLoadDemo}>
                Load demo
              </Button>
            </div>
            <input ref={fileRef} type="file" accept=".csv" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) onFileUpload(f); }} />
          </div>
        ) : (
          /* Loaded state */
          <div className="rounded-lg border p-3 space-y-1.5" style={{ background: SITE_BG[site.id] + "80" }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                <span className="text-xs font-semibold text-green-700">{site.name || "Loaded"}</span>
              </div>
              <button onClick={onClear} className="text-muted-foreground hover:text-foreground">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              {site.vocab.length} unique qualifications · {site.csvData.rows.length} records
            </p>
            <p className="text-xs text-muted-foreground">
              Qual column: <code className="bg-white/60 px-1 rounded text-xs">{site.qCol}</code>
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
